<script lang="ts" setup>
import { addWeeks, format } from 'date-fns'
import { ru } from 'date-fns/locale'

import CustomModalWindow from '~/components/modal-windows/CustomModalWindow.vue'
import constants from '~/constants'
import helpers from '~/helpers'

const model = defineModel<boolean>()

const props = defineProps<{
  price: number
}>()

type Installment = { amount: number, date: string }

const installmentsCount = 4

const installments = computed<Installment[]>(() => {
  const today = new Date()
  const totalAmount = props.price
  const equalPaymentAmount = Math.floor(totalAmount / installmentsCount)
  const lastPaymentAmount = totalAmount - equalPaymentAmount * (installmentsCount - 1)
  const installments = []

  for (let i = 0; i < installmentsCount; ++i) {
    installments.push({
      amount: i === installmentsCount - 1 ? lastPaymentAmount : equalPaymentAmount,
      date: i === 0
        ? 'Сегодня'
        : format(addWeeks(today, i * 2), constants.dateTimeFormats.dateShort, { locale: ru })
    })
  }

  return installments
})

function close (): void {
  model.value = false
}
</script>

<template>
  <CustomModalWindow v-model="model" class="dolyame-modal-window">
    <template #title>
      Оплатите <SvgIcon class="!w-auto" name="payment-methods/dolyame" /> 25% от&nbsp;стоимости покупки
    </template>

    <p>Оставшиеся {{ installmentsCount - 1 }}&nbsp;части спишутся автоматически с&nbsp;шагом в&nbsp;две недели</p>

    <div class="flex flex-col">
      <div class="dolyame-modal-window__features-item flex w-full items-center">
        <svg
          class="dolyame-modal-window__features-item-icon"
          fill="none"
          height="32"
          viewBox="0 0 32 32"
          width="32"
          xmlns="http://www.w3.org/2000/svg"
        >
          <rect fill="#b182bc" height="32" opacity="0.08" rx="4" width="32" />
          <path clip-rule="evenodd" d="M13.8666 11.4666C14.1853 11.4666 14.4736 11.6557 14.6005 11.9481L16.2503 15.7496L20.0518 17.3993C20.3441 17.5262 20.5333 17.8145 20.5333 18.1332C20.5333 18.4519 20.3441 18.7402 20.0518 18.8671L16.2503 20.5169L14.6005 24.3184C14.4736 24.6107 14.1853 24.7999 13.8666 24.7999C13.5479 24.7999 13.2596 24.6107 13.1327 24.3184L11.483 20.5169L7.68146 18.8671C7.38911 18.7402 7.19995 18.4519 7.19995 18.1332C7.19995 17.8145 7.38911 17.5262 7.68146 17.3993L11.483 15.7496L13.1327 11.9481C13.2596 11.6557 13.5479 11.4666 13.8666 11.4666ZM13.8666 14.2761L12.825 16.6762C12.7442 16.8623 12.5957 17.0108 12.4096 17.0916L10.0095 18.1332L12.4096 19.1748C12.5957 19.2556 12.7442 19.4041 12.825 19.5902L13.8666 21.9904L14.9082 19.5902C14.989 19.4041 15.1375 19.2556 15.3236 19.1748L17.7238 18.1332L15.3236 17.0916C15.1375 17.0108 14.989 16.8623 14.9082 16.6762L13.8666 14.2761Z" fill="black" fill-rule="evenodd" />
          <path clip-rule="evenodd" d="M20.5333 6.66675C20.852 6.66675 21.1403 6.85591 21.2672 7.14826L22.1906 9.2761L24.3185 10.1995C24.6108 10.3264 24.8 10.6147 24.8 10.9334C24.8 11.2521 24.6108 11.5404 24.3185 11.6673L22.1906 12.5907L21.2672 14.7186C21.1403 15.0109 20.852 15.2001 20.5333 15.2001C20.2146 15.2001 19.9263 15.0109 19.7995 14.7186L18.876 12.5907L16.7482 11.6673C16.4558 11.5404 16.2667 11.2521 16.2667 10.9334C16.2667 10.6147 16.4558 10.3264 16.7482 10.1995L18.876 9.2761L19.7995 7.14826C19.9263 6.85591 20.2146 6.66675 20.5333 6.66675ZM20.5333 9.47625L20.218 10.2027C20.1373 10.3889 19.9888 10.5374 19.8027 10.6181L19.0762 10.9334L19.8027 11.2487C19.9888 11.3295 20.1373 11.478 20.218 11.6641L20.5333 12.3906L20.8486 11.6641C20.9294 11.478 21.0779 11.3295 21.264 11.2487L21.9905 10.9334L21.264 10.6181C21.0779 10.5374 20.9294 10.3889 20.8486 10.2027L20.5333 9.47625Z" fill="#b182bc" fill-rule="evenodd" />
        </svg>
        Без процентов и&nbsp;комиссий
      </div>
      <div class="dolyame-modal-window__features-item flex w-full items-center">
        <svg
          class="dolyame-modal-window__features-item-icon"
          fill="none"
          height="32"
          viewBox="0 0 32 32"
          width="32"
          xmlns="http://www.w3.org/2000/svg"
        >
          <rect fill="#6f99c6" height="32" opacity="0.08" rx="4" width="32" />
          <path clip-rule="evenodd" d="M18.4 16C18.4 15.5581 18.7581 15.2 19.2 15.2C20.5218 15.2 21.6 16.2781 21.6 17.6C21.6 18.9218 20.5218 20 19.2 20C18.7581 20 18.4 19.6418 18.4 19.2C18.4 18.7581 18.7581 18.4 19.2 18.4C19.6381 18.4 20 18.0381 20 17.6C20 17.1618 19.6381 16.8 19.2 16.8C18.7581 16.8 18.4 16.4418 18.4 16Z" fill="#6f99c6" fill-rule="evenodd" />
          <path clip-rule="evenodd" d="M23.4666 20.8001V11.2001C23.4666 10.611 22.9891 10.1334 22.4 10.1334H9.59996C9.01085 10.1334 8.53329 10.611 8.53329 11.2001V20.8001C8.53329 21.3892 9.01085 21.8668 9.59996 21.8668H22.4C22.9891 21.8668 23.4666 21.3892 23.4666 20.8001ZM9.59996 8.53345C8.1272 8.53345 6.93329 9.72735 6.93329 11.2001V20.8001C6.93329 22.2729 8.1272 23.4668 9.59996 23.4668H22.4C23.8727 23.4668 25.0666 22.2729 25.0666 20.8001V11.2001C25.0666 9.72735 23.8727 8.53345 22.4 8.53345H9.59996Z" fill="black" fill-rule="evenodd" />
          <path clip-rule="evenodd" d="M7.46662 12.5334C7.46662 12.0916 7.82479 11.7334 8.26662 11.7334L21.0666 11.7334C21.5085 11.7334 21.8666 12.0916 21.8666 12.5334C21.8666 12.9753 21.5085 13.3334 21.0666 13.3334L8.26662 13.3334C7.82479 13.3334 7.46662 12.9753 7.46662 12.5334Z" fill="black" fill-rule="evenodd" />
        </svg>
        Как обычная оплата картой
      </div>
    </div>

    <div class="dolyame-modal-window__installments flex justify-between">
      <div v-for="(x, i) in installments" :key="i" class="dolyame-modal-window__installments-item">
        <div class="dolyame-modal-window__installments-item-date">
          {{ x.date }}
        </div>
        <div class="dolyame-modal-window__installments-item-amount">
          {{ helpers.formatPrice(x.amount) }}&nbsp;₽
        </div>
      </div>
    </div>

    <Notification class="mb-4" is-small status="warning">
      При применении промокода заказ нельзя оплатить через «Долями».
    </Notification>

    <p class="dolyame-modal-window__details text-center">
      Подробности на&nbsp;<a href="https://dolyame.ru" rel="nofollow noopener" target="_blank">dolyame.ru</a>
    </p>

    <template #action>
      <a href="#" @click.prevent="close">Закрыть</a>
    </template>
  </CustomModalWindow>
</template>

<style lang="scss" scoped>
@use 'sass:color';

@use 'assets/css/variables';

.dolyame-modal-window {
  &__details {
    color: color.mix(variables.$main-gray-color, white, 50%);
    font-size: 14px;
  }

  &__features-item:not(:last-child) {
    margin-bottom: 12px;
  }

  &__features-item-icon {
    margin-right: 8px;
  }

  &__installments {
    background: variables.$body-background-color;
    border-radius: 12px;
    margin: 16px 0;
    padding: 16px;
  }

  &__installments-item {
    width: 25%;

    &:after {
      background: color.mix(variables.$main-gray-color, white, 20%);
      border-radius: 6px;
      content: '';
      display: block;
      height: 5px;
      width: 95%;
    }

    &:first-child:after {
      background: variables.$main-blue-color;
    }

    &:not(:first-child) {
      color: color.mix(variables.$main-gray-color, white, 50%);
    }
  }

  &__installments-item-amount {
    font-weight: 700;
    margin-bottom: 12px;
  }

  &__installments-item-date {
    font-size: 14px;
  }
}
</style>
